import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    ArrowLeft, 
    Heart, 
    Brain, 
    Shield, 
    Sparkles,
    Check,
    Send,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { CoRegulateLogo } from '../components/Logo';
import { AdminEditButton } from '../components/admin/AdminEditButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping for dynamic icons from database
const ICON_MAP = {
    'Brain': Brain,
    'Heart': Heart,
    'Shield': Shield,
    'Sparkles': Sparkles
};

const Hypnotherapy = () => {
    const navigate = useNavigate();
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [enquirySubmitted, setEnquirySubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [enquiryForm, setEnquiryForm] = useState({
        name: '',
        email: '',
        phone: '',
        concerns: '',
        preferredContact: 'email'
    });

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/pages/hypnotherapy`);
                if (response.ok) {
                    const data = await response.json();
                    setPageContent(data);
                }
            } catch (error) {
                console.error('Error fetching hypnotherapy content:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const handleEnquirySubmit = async (e) => {
        e.preventDefault();
        
        if (!enquiryForm.name || !enquiryForm.email) {
            toast.error('Please fill in your name and email');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/hypnotherapy-enquiry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...enquiryForm,
                    source: 'hypnotherapy_info_page'
                })
            });
            
            if (response.ok) {
                setEnquirySubmitted(true);
                toast.success('Enquiry submitted successfully!');
            }
        } catch (error) {
            console.error('Enquiry submission failed:', error);
            setEnquirySubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FEF6EF] to-[#FFECD9]">
                <Loader2 className="w-8 h-8 text-[#1D6B6B] animate-spin" />
            </div>
        );
    }

    // Get sections by type
    const visibleSections = pageContent?.sections?.filter(s => s.is_visible !== false).sort((a, b) => a.order - b.order) || [];
    const introSection = visibleSections.find(s => s.type === 'intro');
    const benefitSections = visibleSections.filter(s => s.type === 'benefit');
    const faqSections = visibleSections.filter(s => s.type === 'faq');

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FFECD9]">
            {/* SEO Meta Tags */}
            <Helmet>
                <title>Clinical Hypnotherapy for ADHD | CoRegulate</title>
                <meta name="description" content="Learn how clinical hypnotherapy can help with ADHD emotional regulation. Evidence-based, gentle approach working with your subconscious mind to build new patterns." />
                <link rel="canonical" href="https://coregulateadhd.com/hypnotherapy" />
                <meta property="og:title" content="Clinical Hypnotherapy for ADHD | CoRegulate" />
                <meta property="og:description" content="Evidence-based hypnotherapy for ADHD emotional regulation and lasting change." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://coregulateadhd.com/hypnotherapy" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="Clinical Hypnotherapy for ADHD" />
                <meta name="twitter:description" content="Gentle, evidence-based approach to ADHD emotional regulation." />
                
                {/* Service Schema */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "serviceType": "Clinical Hypnotherapy",
                        "provider": {
                            "@type": "Organization",
                            "name": "CoRegulate",
                            "url": "https://coregulateadhd.com"
                        },
                        "description": "Clinical hypnotherapy services specializing in ADHD emotional regulation and anxiety management.",
                        "areaServed": "Worldwide",
                        "hasOfferCatalog": {
                            "@type": "OfferCatalog",
                            "name": "Hypnotherapy Services",
                            "itemListElement": [
                                {
                                    "@type": "Offer",
                                    "itemOffered": {
                                        "@type": "Service",
                                        "name": "ADHD Emotional Regulation Hypnotherapy"
                                    }
                                }
                            ]
                        }
                    }
                `}</script>
                
                {/* Breadcrumb Schema */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            {
                                "@type": "ListItem",
                                "position": 1,
                                "name": "Home",
                                "item": "https://coregulateadhd.com"
                            },
                            {
                                "@type": "ListItem",
                                "position": 2,
                                "name": "Hypnotherapy",
                                "item": "https://coregulateadhd.com/hypnotherapy"
                            }
                        ]
                    }
                `}</script>
            </Helmet>
            
            {/* Admin Edit Button */}
            <AdminEditButton tab="pages" />

            {/* Header */}
            <header className="glass-header sticky top-0 z-10">
                <Container size="md" className="py-4 flex items-center gap-4">
                    <IconButton onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <CoRegulateLogo size="sm" onClick={() => navigate('/home')} className="cursor-pointer" />
                </Container>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8 pb-20">
                {/* Hero Section */}
                <section className="text-center mb-12">
                    <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-6">
                        <Heart className="w-10 h-10" />
                    </IconContainer>
                    <Heading as="h1" size="text-3xl" className="mb-4 text-[#1D6B6B]">
                        {pageContent?.title || 'Understanding Hypnotherapy'}
                    </Heading>
                    <BodyText className="text-[#7D8E95] max-w-xl mx-auto leading-relaxed">
                        {pageContent?.hero_text || 'A gentle, evidence-based approach to lasting change — working with your subconscious mind to heal, grow, and build new patterns.'}
                    </BodyText>
                </section>

                {/* What is Hypnotherapy - Intro Section */}
                {introSection && (
                    <section className="mb-12">
                        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/40/30">
                            <Heading as="h2" size="text-xl" className="mb-4 text-[#1D6B6B]">
                                {introSection.title}
                            </Heading>
                            <div className="space-y-4 text-[#7D8E95] leading-relaxed">
                                {introSection.content.split('\n\n').map((paragraph, idx) => (
                                    <BodyText key={idx}>{paragraph}</BodyText>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Benefits */}
                {benefitSections.length > 0 && (
                    <section className="mb-12">
                        <Heading as="h2" size="text-xl" className="mb-6 text-[#1D6B6B] text-center">
                            Why Hypnotherapy Works
                        </Heading>
                        <div className="grid md:grid-cols-2 gap-4">
                            {benefitSections.map((benefit) => {
                                const Icon = benefit.icon ? ICON_MAP[benefit.icon] : Heart;
                                return (
                                    <div 
                                        key={benefit.section_id}
                                        className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-white/40/30"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center flex-shrink-0">
                                                {Icon && <Icon className="w-5 h-5 text-[#1D6B6B]" />}
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-[#1D6B6B] mb-1">{benefit.title}</h3>
                                                <SmallText className="text-[#7D8E95]">{benefit.content}</SmallText>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* FAQs */}
                {faqSections.length > 0 && (
                    <section className="mb-12">
                        <Heading as="h2" size="text-xl" className="mb-6 text-[#1D6B6B] text-center">
                            Common Questions
                        </Heading>
                        <div className="space-y-3">
                            {faqSections.map((faq, idx) => (
                                <div 
                                    key={faq.section_id}
                                    className="bg-white/60 backdrop-blur-sm rounded-xl border border-white/40/30 overflow-hidden"
                                >
                                    <button
                                        onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                                        className="w-full px-5 py-4 flex items-center justify-between text-left"
                                    >
                                        <span className="font-medium text-[#1D6B6B] pr-4">{faq.title}</span>
                                        {expandedFaq === idx ? (
                                            <ChevronUp className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                        )}
                                    </button>
                                    {expandedFaq === idx && (
                                        <div className="px-5 pb-4">
                                            <BodyText className="text-[#7D8E95] leading-relaxed">
                                                {faq.content}
                                            </BodyText>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Enquiry Form */}
                <section className="mb-12">
                    <div className="bg-[#1D6B6B]/5 rounded-2xl p-6 md:p-8 border border-[#1D6B6B]/10">
                        {!enquirySubmitted ? (
                            <>
                                <div className="text-center mb-6">
                                    <Heading as="h2" size="text-xl" className="mb-2 text-[#1D6B6B]">
                                        Interested in 1:1 Hypnotherapy?
                                    </Heading>
                                    <BodyText className="text-[#7D8E95]">
                                        Fill in your details and a qualified hypnotherapist will be in touch to discuss how they can help.
                                    </BodyText>
                                </div>

                                <form onSubmit={handleEnquirySubmit} className="space-y-4 max-w-md mx-auto">
                                    <div>
                                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Your Name *</label>
                                        <input
                                            type="text"
                                            required
                                            value={enquiryForm.name}
                                            onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})}
                                            className="w-full px-4 py-3 rounded-xl bg-white border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                            placeholder="Enter your name"
                                            data-testid="enquiry-name"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Email Address *</label>
                                        <input
                                            type="email"
                                            required
                                            value={enquiryForm.email}
                                            onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})}
                                            className="w-full px-4 py-3 rounded-xl bg-white border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                            placeholder="your@email.com"
                                            data-testid="enquiry-email"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Phone (optional)</label>
                                        <input
                                            type="tel"
                                            value={enquiryForm.phone}
                                            onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})}
                                            className="w-full px-4 py-3 rounded-xl bg-white border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30"
                                            placeholder="Your phone number"
                                            data-testid="enquiry-phone"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">What would you like support with?</label>
                                        <textarea
                                            value={enquiryForm.concerns}
                                            onChange={(e) => setEnquiryForm({...enquiryForm, concerns: e.target.value})}
                                            rows={3}
                                            className="w-full px-4 py-3 rounded-xl bg-white border border-white/40/30 text-[#1D6B6B] placeholder-[#7D8E95] focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/30 resize-none"
                                            placeholder="e.g., anxiety, emotional regulation, trauma, limiting beliefs..."
                                            data-testid="enquiry-concerns"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-[#1D6B6B] mb-2">Preferred contact method</label>
                                        <div className="flex gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setEnquiryForm({...enquiryForm, preferredContact: 'email'})}
                                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                                    enquiryForm.preferredContact === 'email'
                                                        ? 'bg-[#1D6B6B] text-white'
                                                        : 'bg-white text-[#7D8E95] border border-white/40/30'
                                                }`}
                                            >
                                                Email
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEnquiryForm({...enquiryForm, preferredContact: 'phone'})}
                                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                                    enquiryForm.preferredContact === 'phone'
                                                        ? 'bg-[#1D6B6B] text-white'
                                                        : 'bg-white text-[#7D8E95] border border-white/40/30'
                                                }`}
                                            >
                                                Phone
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <PrimaryButton
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full"
                                        data-testid="submit-enquiry-btn"
                                    >
                                        {isSubmitting ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4 mr-2" />
                                                Submit Enquiry
                                            </>
                                        )}
                                    </PrimaryButton>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-[#81C784]/20 flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-8 h-8 text-[#81C784]" />
                                </div>
                                <Heading as="h3" size="text-xl" className="mb-2 text-[#1D6B6B]">
                                    Enquiry Submitted!
                                </Heading>
                                <BodyText className="text-[#7D8E95] mb-4">
                                    Thank you for your interest. A hypnotherapist will be in touch within 24-48 hours.
                                </BodyText>
                                <SecondaryButton onClick={() => navigate('/home')}>
                                    Return to Dashboard
                                </SecondaryButton>
                            </div>
                        )}
                    </div>
                </section>

                {/* Mindgain Link - Subtle */}
                <section className="text-center">
                    <div className="inline-block bg-white/40 rounded-xl px-6 py-4 border border-white/40/20">
                        <SmallText className="text-[#7D8E95] mb-2 block">
                            Want to learn more about our hypnotherapy services?
                        </SmallText>
                        <a 
                            href="https://mindgaintherapies.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#1D6B6B] hover:text-[#145555] font-medium text-sm transition-colors"
                            data-testid="mindgain-link"
                        >
                            Visit Mindgain Therapies
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Hypnotherapy;
